import websocketService from 'services/websocketService';

const inflight = new Set<string>();

const toKey = (pageName: string | string[]) =>
  Array.isArray(pageName) ? pageName.join('|') : pageName;

// Routes where a counselor/parent reviews a specific student; the trailing
// segment is the reviewed student's id (same value as useParams().id).
const REVIEWED_STUDENT_ROUTE =
  /\/(?:application\/counselor|application\/summary\/[^/?#]+|dashboard\/student)\/(\d+)(?:[/?#]|$)/;

// Read the reviewed student id from the current route (HashRouter or BrowserRouter).
export function getReviewedStudentIdFromRoute(): number | null {
  if (typeof window === 'undefined') return null;
  const source = `${window.location.hash || ''} ${window.location.pathname || ''}`;
  const match = source.match(REVIEWED_STUDENT_ROUTE);
  const id = match ? Number(match[1]) : NaN;
  return Number.isFinite(id) && id > 0 ? id : null;
}

export async function toggleReviewedAndComplete(
  pageName: string | string[],
  current: boolean,
  setValue: (next: boolean) => void,
  studentId?: number | string | null
): Promise<void> {
  const key = toKey(pageName);
  if (inflight.has(key)) return;

  const next = !current;
  inflight.add(key);
  setValue(next);

  const pages = Array.isArray(pageName) ? pageName : [pageName];
  try {
    await Promise.all(
      pages.map((name) => websocketService.finishReviewAndComplete(name, next, studentId))
    );
  } catch (error) {
    setValue(current);
    console.error('Reviewed & Complete failed to persist', error);
  } finally {
    inflight.delete(key);
  }
}

// The target student is the one being reviewed: explicit `studentId` first,
// then the route id (/application/counselor/:id, /application/summary/*/:id).
// Only if both are missing does the service fall back to the socket session,
// which can be empty mid-switch or point at a previously selected student.
export async function toggleCounselorReviewedAndComplete(
  pageName: string | string[],
  current: boolean,
  setValue: (next: boolean) => void,
  explicitStudentId?: number | string | null
): Promise<void> {
  const studentId = explicitStudentId ?? getReviewedStudentIdFromRoute();
  const key = `counselor:${studentId ?? ''}:${toKey(pageName)}`;
  if (inflight.has(key)) return;

  const next = !current;
  inflight.add(key);
  setValue(next);

  const pages = Array.isArray(pageName) ? pageName : [pageName];
  try {
    await Promise.all(
      pages.map((name) =>
        websocketService.finishReviewAndCompleteCounselor(name, next, studentId)
      )
    );
  } catch (error) {
    setValue(current);
    console.error('Counselor Reviewed & Complete failed to persist', error);
  } finally {
    inflight.delete(key);
  }
}
